import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentModel } from 'api/documents/documents.model';
import { ToggleFavouriteButton } from 'components/Buttons';
import useCertificateHelper from 'hooks/useCertificateHelper';
import { CertificateDetailsModel } from 'models/certificate.model';
import { ADDITIONAL_DETAILS, NESTED_DETAILS } from 'models/certificate.model';
import { CATEGORY_TYPE } from 'models/documents.model';

import * as Styled from './CertificatesPage.styles';

const useCertificatesTableConfig = (
  isInteractable: boolean
): ColumnsType<DocumentModel<CertificateDetailsModel>> => {
  const { t } = useTranslation();
  const { getValueByLabel } = useCertificateHelper();

  const setNameByCategory = useCallback(
    (category: CATEGORY_TYPE, additionalDetails = {} as CertificateDetailsModel) => {
      switch (category) {
        case CATEGORY_TYPE.CHEMISTRY:
          return additionalDetails[ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME] || '-';
        case CATEGORY_TYPE.BATTERY_CELLS:
          return getValueByLabel(
            NESTED_DETAILS.BATTERY_CELL_MODEL,
            additionalDetails[ADDITIONAL_DETAILS.DETAILS],
            '-'
          );
        case CATEGORY_TYPE.BATTERY_PACKS:
        case CATEGORY_TYPE.BATTERY_TECH_REVIEW:
        case CATEGORY_TYPE.BATTERY_PACKS_REPAIR:
        case CATEGORY_TYPE.BATTERY_REPURPOSE:
          return getValueByLabel(
            NESTED_DETAILS.EVMODEL,
            additionalDetails[ADDITIONAL_DETAILS.DETAILS],
            '-'
          );
        case CATEGORY_TYPE.BATTERY_PACKS_RECYCLING:
        case CATEGORY_TYPE.BATTERY_WASTE:
          return getValueByLabel(
            NESTED_DETAILS.BATTERY_CELL_MODEL,
            additionalDetails[ADDITIONAL_DETAILS.DETAILS],
            '-'
          );

        default:
          return additionalDetails.productName;
      }
    },
    [getValueByLabel]
  );

  const tableConfig = useMemo<ColumnsType<DocumentModel<CertificateDetailsModel>>>(
    () => [
      {
        title: t('certificate.typeOfCertificate'),
        dataIndex: 'category',
        render: (category, { jobId, favourite }) => (
          <Styled.CertificateName>
            <ToggleFavouriteButton
              jobId={jobId}
              isFavourite={favourite}
              isClickable={isInteractable}
            />{' '}
            {t(
              category !== CATEGORY_TYPE.CERTIFICATE
                ? `certificate.${category}`
                : 'certificate.other'
            )}
          </Styled.CertificateName>
        ),
      },
      {
        title: t('certificate.name'),
        dataIndex: 'category',
        render: (category, { additionalDetails }) => setNameByCategory(category, additionalDetails),
      },
      {
        title: t('certificate.certificateUID'),
        dataIndex: ['additionalDetails', 'productId'],
      },
      {
        title: t('certificate.company'),
        dataIndex: ['additionalDetails', 'company'],
      },
      {
        title: t('certificate.dateCreated'),
        dataIndex: 'publicationDate',
        render: (publicationDate: string) => (
          <>
            {publicationDate
              ? moment(publicationDate).format('DD-MM-YYYY')
              : t('document.publishingStatus')}
          </>
        ),
      },
    ],
    [isInteractable, setNameByCategory, t]
  );

  return tableConfig.filter(({ title }) => title !== (isInteractable ? '' : 'Structure'));
};

export default useCertificatesTableConfig;
